/**
 * Full Review Interface Component
 * Comprehensive subdomain-by-subdomain review with dimension scoring
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  XCircle,
  Loader2,
  Save,
  ThumbsUp,
  ThumbsDown,
  Lightbulb,
} from 'lucide-react';
import { startReview, submitSubdomainReview, completeFullReview } from '../../lib/api/domainPacksApi';

interface FullReviewInterfaceProps {
  domainPackId: string;
  domainName: string;
  reviewerName: string;
  reviewerEmail: string;
  onComplete: () => void;
  onCancel: () => void;
}

interface SubdomainData {
  subdomainId: string;
  subdomainName: string;
  overallScore: number;
  dimensions: Array<{
    dimension: string;
    score: number;
  }>;
  issues: Array<{
    issueId: string;
    severity: string;
    title: string;
    description: string;
  }>;
}

interface SubdomainReviewData {
  reviewerOverallScore: number;
  dimensionScores: Record<string, number>;
  dimensionFeedback: Record<string, string>;
  notes: string;
  acknowledgedIssues: string[];
}

const FullReviewInterface: React.FC<FullReviewInterfaceProps> = ({
  domainPackId,
  domainName,
  reviewerName,
  reviewerEmail,
  onComplete,
  onCancel,
}) => {
  const queryClient = useQueryClient();

  // State
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [subdomains, setSubdomains] = useState<SubdomainData[]>([]);
  const [currentSubdomainIndex, setCurrentSubdomainIndex] = useState(0);
  const [subdomainReviews, setSubdomainReviews] = useState<Record<string, SubdomainReviewData>>({});
  const [showSummary, setShowSummary] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Summary state
  const [strengths, setStrengths] = useState<string[]>([]);
  const [weaknesses, setWeaknesses] = useState<string[]>([]);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [overallNotes, setOverallNotes] = useState('');
  const [newStrength, setNewStrength] = useState('');
  const [newWeakness, setNewWeakness] = useState('');
  const [newRecommendation, setNewRecommendation] = useState('');

  // Start review mutation
  const startReviewMutation = useMutation({
    mutationFn: () =>
      startReview(domainPackId, {
        review_mode: 'full',
        reviewer_name: reviewerName,
        reviewer_email: reviewerEmail,
      }),
    onSuccess: (data) => {
      console.log('Full Review Start Response:', data);
      setReviewId(data.review_id);

      // Map backend response (snake_case) to frontend (camelCase)
      const mappedSubdomains = (data.subdomains || []).map((sub: any) => ({
        subdomainId: sub.subdomain_id || sub.subdomainId,
        subdomainName: sub.subdomain_name || sub.subdomainName,
        overallScore: sub.overall_score || sub.overallScore || 0,
        dimensions: (sub.dimensions || []).map((dim: any) => ({
          dimension: dim.dimension,
          score: dim.score || 0,
        })),
        issues: (sub.issues || []).map((issue: any) => ({
          issueId: issue.issue_id || issue.issueId,
          severity: issue.severity,
          title: issue.title,
          description: issue.description,
        })),
      }));

      setSubdomains(mappedSubdomains);
      setStartTime(new Date());

      // Initialize review data for each subdomain
      const initialReviews: Record<string, SubdomainReviewData> = {};
      mappedSubdomains.forEach((subdomain: SubdomainData) => {
        const dimScores: Record<string, number> = {};
        const dimFeedback: Record<string, string> = {};
        subdomain.dimensions.forEach((dim) => {
          dimScores[dim.dimension] = dim.score;
          dimFeedback[dim.dimension] = '';
        });

        initialReviews[subdomain.subdomainId] = {
          reviewerOverallScore: subdomain.overallScore,
          dimensionScores: dimScores,
          dimensionFeedback: dimFeedback,
          notes: '',
          acknowledgedIssues: [],
        };
      });
      setSubdomainReviews(initialReviews);
    },
    onError: (error: any) => {
      console.error('Failed to start full review:', error);
      alert(`Failed to start review: ${error.message || 'Unknown error'}`);
      onCancel();
    },
  });

  // Submit subdomain review mutation
  const submitSubdomainMutation = useMutation({
    mutationFn: (data: { subdomainId: string; reviewData: any }) =>
      submitSubdomainReview(reviewId!, data.subdomainId, data.reviewData),
  });

  // Complete full review mutation
  const completeReviewMutation = useMutation({
    mutationFn: (outcome: 'approved' | 'flagged' | 'rejected') =>
      completeFullReview(reviewId!, {
        review_id: reviewId!,
        overall_outcome: outcome,
        strengths,
        weaknesses,
        recommendations,
        overall_notes: overallNotes || undefined,
        time_spent_seconds: Math.floor(elapsedTime),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain-pack-quality', domainPackId] });
      onComplete();
    },
    onError: (error: any) => {
      alert(`Failed to complete review: ${error.message || 'Unknown error'}`);
    },
  });

  // Start review on mount
  useEffect(() => {
    startReviewMutation.mutate();
  }, []);

  // Timer effect
  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime.getTime()) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  // Format elapsed time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Current subdomain data
  const currentSubdomain = subdomains[currentSubdomainIndex];
  const currentReview = currentSubdomain
    ? subdomainReviews[currentSubdomain.subdomainId]
    : null;

  // Update current review data
  const updateCurrentReview = (updates: Partial<SubdomainReviewData>) => {
    if (!currentSubdomain) return;

    setSubdomainReviews({
      ...subdomainReviews,
      [currentSubdomain.subdomainId]: {
        ...currentReview!,
        ...updates,
      },
    });
  };

  // Update dimension score
  const updateDimensionScore = (dimension: string, score: number) => {
    updateCurrentReview({
      dimensionScores: {
        ...currentReview!.dimensionScores,
        [dimension]: score,
      },
    });
  };

  // Update dimension feedback
  const updateDimensionFeedback = (dimension: string, feedback: string) => {
    updateCurrentReview({
      dimensionFeedback: {
        ...currentReview!.dimensionFeedback,
        [dimension]: feedback,
      },
    });
  };

  // Toggle issue acknowledgment
  const toggleIssueAcknowledgment = (issueId: string) => {
    const current = currentReview!.acknowledgedIssues;
    const updated = current.includes(issueId)
      ? current.filter((id) => id !== issueId)
      : [...current, issueId];

    updateCurrentReview({ acknowledgedIssues: updated });
  };

  // Navigation
  const canGoNext = currentSubdomainIndex < subdomains.length - 1;
  const canGoPrevious = currentSubdomainIndex > 0;

  const handleNext = async () => {
    // Save current subdomain review
    if (currentSubdomain && currentReview) {
      await submitSubdomainMutation.mutateAsync({
        subdomainId: currentSubdomain.subdomainId,
        reviewData: {
          reviewer_overall_score: currentReview.reviewerOverallScore,
          dimension_scores: currentReview.dimensionScores,
          dimension_feedback: currentReview.dimensionFeedback,
          notes: currentReview.notes,
          acknowledged_issues: currentReview.acknowledgedIssues,
        },
      });
    }

    if (canGoNext) {
      setCurrentSubdomainIndex(currentSubdomainIndex + 1);
    } else {
      // All subdomains reviewed, show summary
      setShowSummary(true);
    }
  };

  const handlePrevious = () => {
    if (canGoPrevious) {
      setCurrentSubdomainIndex(currentSubdomainIndex - 1);
    }
  };

  const handleSave = async () => {
    if (currentSubdomain && currentReview) {
      await submitSubdomainMutation.mutateAsync({
        subdomainId: currentSubdomain.subdomainId,
        reviewData: {
          reviewer_overall_score: currentReview.reviewerOverallScore,
          dimension_scores: currentReview.dimensionScores,
          dimension_feedback: currentReview.dimensionFeedback,
          notes: currentReview.notes,
          acknowledged_issues: currentReview.acknowledgedIssues,
        },
      });
      alert('Subdomain review saved!');
    }
  };

  const handleOutcome = async (outcome: 'approved' | 'flagged' | 'rejected') => {
    const confirmMsg = `${outcome.charAt(0).toUpperCase() + outcome.slice(1)} this domain pack?`;
    if (!confirm(confirmMsg)) return;

    completeReviewMutation.mutate(outcome);
  };

  // Calculate overall reviewer score
  const calculateOverallReviewerScore = () => {
    const scores = Object.values(subdomainReviews).map((r) => r.reviewerOverallScore);
    if (scores.length === 0) return 0;
    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
  };

  // Calculate overall AI score
  const calculateOverallAIScore = () => {
    if (subdomains.length === 0) return 0;
    const scores = subdomains.map((s) => s.overallScore);
    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
  };

  // Loading state
  if (startReviewMutation.isPending || !reviewId) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        <span className="ml-3 text-neutral-600">Starting full review...</span>
      </div>
    );
  }

  // Show summary screen
  if (showSummary) {
    const overallReviewerScore = calculateOverallReviewerScore();
    const overallAIScore = calculateOverallAIScore();
    const scoreAgreement = Math.abs(overallReviewerScore - overallAIScore) <= 10;

    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-6 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-neutral-900 mb-6">Review Summary</h2>

        {/* Overall Scores */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
            <div className="text-sm text-primary-700 mb-1">Your Overall Score</div>
            <div className="text-3xl font-bold text-primary-900">{overallReviewerScore.toFixed(1)}/100</div>
          </div>
          <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
            <div className="text-sm text-neutral-700 mb-1">AI Overall Score</div>
            <div className="text-3xl font-bold text-neutral-900">{overallAIScore.toFixed(1)}/100</div>
          </div>
        </div>

        {/* Score Agreement */}
        <div className={`mb-8 p-4 rounded-lg border ${scoreAgreement ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="flex items-center gap-2">
            {scoreAgreement ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            )}
            <span className={`font-medium ${scoreAgreement ? 'text-green-900' : 'text-yellow-900'}`}>
              Score Agreement: {scoreAgreement ? 'Within 10 points ✓' : 'Differs by more than 10 points'}
            </span>
          </div>
        </div>

        {/* Strengths */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            <ThumbsUp className="w-4 h-4 inline mr-1" />
            Key Strengths
          </label>
          <div className="space-y-2 mb-3">
            {strengths.map((strength, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded p-2">
                <span className="flex-1 text-sm text-neutral-700">+ {strength}</span>
                <button
                  onClick={() => setStrengths(strengths.filter((_, i) => i !== idx))}
                  className="text-red-600 hover:text-red-700"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newStrength}
              onChange={(e) => setNewStrength(e.target.value)}
              placeholder="Add a strength..."
              className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newStrength.trim()) {
                  setStrengths([...strengths, newStrength.trim()]);
                  setNewStrength('');
                }
              }}
            />
            <button
              onClick={() => {
                if (newStrength.trim()) {
                  setStrengths([...strengths, newStrength.trim()]);
                  setNewStrength('');
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Add
            </button>
          </div>
        </div>

        {/* Weaknesses */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            <ThumbsDown className="w-4 h-4 inline mr-1" />
            Key Weaknesses
          </label>
          <div className="space-y-2 mb-3">
            {weaknesses.map((weakness, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded p-2">
                <span className="flex-1 text-sm text-neutral-700">- {weakness}</span>
                <button
                  onClick={() => setWeaknesses(weaknesses.filter((_, i) => i !== idx))}
                  className="text-red-600 hover:text-red-700"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newWeakness}
              onChange={(e) => setNewWeakness(e.target.value)}
              placeholder="Add a weakness..."
              className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newWeakness.trim()) {
                  setWeaknesses([...weaknesses, newWeakness.trim()]);
                  setNewWeakness('');
                }
              }}
            />
            <button
              onClick={() => {
                if (newWeakness.trim()) {
                  setWeaknesses([...weaknesses, newWeakness.trim()]);
                  setNewWeakness('');
                }
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Add
            </button>
          </div>
        </div>

        {/* Recommendations */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            <Lightbulb className="w-4 h-4 inline mr-1" />
            Recommendations
          </label>
          <div className="space-y-2 mb-3">
            {recommendations.map((rec, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded p-2">
                <span className="flex-1 text-sm text-neutral-700">→ {rec}</span>
                <button
                  onClick={() => setRecommendations(recommendations.filter((_, i) => i !== idx))}
                  className="text-red-600 hover:text-red-700"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newRecommendation}
              onChange={(e) => setNewRecommendation(e.target.value)}
              placeholder="Add a recommendation..."
              className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newRecommendation.trim()) {
                  setRecommendations([...recommendations, newRecommendation.trim()]);
                  setNewRecommendation('');
                }
              }}
            />
            <button
              onClick={() => {
                if (newRecommendation.trim()) {
                  setRecommendations([...recommendations, newRecommendation.trim()]);
                  setNewRecommendation('');
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add
            </button>
          </div>
        </div>

        {/* Overall Notes */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-neutral-700 mb-2">Overall Notes (optional)</label>
          <textarea
            value={overallNotes}
            onChange={(e) => setOverallNotes(e.target.value)}
            placeholder="Add any overall comments about the domain pack..."
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
            rows={4}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-neutral-200">
          <button
            onClick={() => setShowSummary(false)}
            className="flex items-center gap-2 px-4 py-2 text-neutral-600 hover:text-neutral-800"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Review
          </button>

          <div className="flex gap-3">
            <button
              onClick={() => handleOutcome('rejected')}
              disabled={completeReviewMutation.isPending}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {completeReviewMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              Reject
            </button>

            <button
              onClick={() => handleOutcome('flagged')}
              disabled={completeReviewMutation.isPending}
              className="flex items-center gap-2 px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              {completeReviewMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <AlertTriangle className="w-4 h-4" />
              )}
              Flag
            </button>

            <button
              onClick={() => handleOutcome('approved')}
              disabled={completeReviewMutation.isPending}
              className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {completeReviewMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Approve
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main subdomain review interface
  if (!currentSubdomain || !currentReview) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
          <p className="text-neutral-600">No subdomains to review</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-neutral-200">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Full Review: {domainName}</h2>
          <p className="text-sm text-neutral-600 mt-1">
            Progress: {currentSubdomainIndex + 1}/{subdomains.length} Subdomains Reviewed
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-mono text-primary-600">⏱ {formatTime(elapsedTime)}</div>
          <div className="text-xs text-neutral-500">Elapsed Time</div>
        </div>
      </div>

      {/* Subdomain Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {subdomains.map((subdomain, idx) => (
          <button
            key={subdomain.subdomainId}
            onClick={() => setCurrentSubdomainIndex(idx)}
            className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-colors ${
              idx === currentSubdomainIndex
                ? 'bg-primary-600 text-white'
                : idx < currentSubdomainIndex
                ? 'bg-green-100 text-green-800 border border-green-300'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            {subdomain.subdomainName}
            {idx < currentSubdomainIndex && ' ✓'}
          </button>
        ))}
      </div>

      {/* Current Subdomain Content */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-neutral-900 mb-4">📊 {currentSubdomain.subdomainName}</h3>

        {/* AI Score Display */}
        <div className="bg-neutral-50 rounded-lg p-4 mb-6 border border-neutral-200">
          <div className="text-sm text-neutral-600 mb-1">AI Overall Score</div>
          <div className="text-3xl font-bold text-neutral-900">{currentSubdomain.overallScore}/100</div>
        </div>

        {/* Reviewer Overall Score */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-neutral-700 mb-2">Your Overall Score</label>
          <input
            type="number"
            min="0"
            max="100"
            value={currentReview.reviewerOverallScore}
            onChange={(e) => updateCurrentReview({ reviewerOverallScore: Number(e.target.value) })}
            className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-2xl font-bold"
          />
          <p className="text-xs text-neutral-500 mt-1">Score from 0-100</p>
        </div>

        {/* Dimension Scores */}
        <div className="mb-6">
          <h4 className="font-semibold text-neutral-900 mb-3">📐 Dimension Scores</h4>
          <div className="space-y-4">
            {currentSubdomain.dimensions.map((dim) => (
              <div key={dim.dimension} className="border border-neutral-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-4">
                    <span className="font-medium text-neutral-900 capitalize">{dim.dimension}</span>
                    <span className="text-sm text-neutral-600">AI: {dim.score}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-neutral-600">Your Score:</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={currentReview.dimensionScores[dim.dimension] || dim.score}
                      onChange={(e) => updateDimensionScore(dim.dimension, Number(e.target.value))}
                      className="w-20 px-2 py-1 border border-neutral-300 rounded"
                    />
                  </div>
                </div>
                <textarea
                  value={currentReview.dimensionFeedback[dim.dimension] || ''}
                  onChange={(e) => updateDimensionFeedback(dim.dimension, e.target.value)}
                  placeholder={`Feedback on ${dim.dimension}...`}
                  className="w-full mt-2 px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                  rows={2}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Issues */}
        {currentSubdomain.issues && currentSubdomain.issues.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold text-neutral-900 mb-3">🚨 Issues ({currentSubdomain.issues.length})</h4>
            <div className="space-y-2">
              {currentSubdomain.issues.map((issue) => {
                const isAcknowledged = currentReview.acknowledgedIssues.includes(issue.issueId);

                return (
                  <div
                    key={issue.issueId}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      isAcknowledged
                        ? 'bg-green-50 border-green-300'
                        : 'bg-red-50 border-red-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isAcknowledged}
                      onChange={() => toggleIssueAcknowledgment(issue.issueId)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                          issue.severity === 'critical'
                            ? 'bg-red-100 text-red-800'
                            : issue.severity === 'major'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {issue.severity.toUpperCase()}
                        </span>
                        <span className="font-medium text-neutral-900">{issue.title}</span>
                      </div>
                      <p className="text-sm text-neutral-700">{issue.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* General Notes */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">General Notes</label>
          <textarea
            value={currentReview.notes}
            onChange={(e) => updateCurrentReview({ notes: e.target.value })}
            placeholder="Overall thoughts on this subdomain..."
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
            rows={3}
          />
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-neutral-200">
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-neutral-600 hover:text-neutral-800 border border-neutral-300 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            className="flex items-center gap-2 px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={submitSubdomainMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {submitSubdomainMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>

          <button
            onClick={handleNext}
            disabled={submitSubdomainMutation.isPending}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {submitSubdomainMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : canGoNext ? (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            ) : (
              <>
                Review Summary
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FullReviewInterface;
